import { cva, type VariantProps } from 'class-variance-authority';
import { Orbit } from 'loading-dev';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/cn';

// Botão do sistema (WEB-133): PÍLULA chapada. Uma ação primária por tela, em tinta (papel
// no escuro); o resto é neutro. Ouro nunca é fundo de botão.
//
// Foco: o contorno de 2px vem da regra base de `index.css` (`:focus-visible`, tinta no
// claro e ouro no escuro). Por isso aqui não há `outline-none` nem anel próprio.
//
// `min-h-10 min-w-10` até `sm`: área de toque de 40px no celular (`spec/17` §7 item 12,
// cobrado por `e2e/responsive.spec.ts`, que mede a caixa real).
const buttonVariants = cva(
  "inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center gap-2 rounded-full text-sm font-medium whitespace-nowrap transition-[background-color,color,box-shadow,opacity,scale] duration-150 ease-out-strong select-none active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 sm:min-h-0 sm:min-w-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // A ação primária: pílula de tinta (de papel no escuro).
        default: 'bg-primary text-primary-foreground hover:bg-primary/88',
        // Ação destrutiva de ENTRADA (abre a confirmação): neutra, só o texto diz o risco.
        destructive:
          'bg-secondary/60 text-destructive shadow-(--shadow-inset) hover:bg-secondary aria-expanded:bg-secondary',
        // Confirmação destrutiva FINAL (o clique que apaga): a única pílula de cor cheia.
        'destructive-solid': 'bg-destructive text-destructive-foreground hover:opacity-90',
        // Secundária com fio: célula clara + contorno de 1px, a mesma casca dos campos.
        outline:
          'bg-secondary/60 text-foreground shadow-(--shadow-inset) hover:bg-secondary aria-expanded:bg-secondary',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-accent',
        ghost: 'text-foreground hover:bg-secondary aria-expanded:bg-secondary',
        link: 'text-teal underline-offset-4 hover:text-teal-hover hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 px-3 text-13 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Trava o botão e põe o spinner do sistema ANTES do rótulo (o texto continua). */
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-busy={loading || undefined}
      disabled={loading || disabled}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {/* Tamanho por `size`: o CSS do loading-dev fica fora de @layer. */}
          {loading && <Orbit size={14} color="currentColor" />}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
